import type { ReactElement } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../shared/auth/useAuth.js';
import { BrandMark } from '../shared/illustrations/BrandMark.js';
import { DoorTransitionProvider } from '../shared/illustrations/DoorTransition.js';
import { PageBackdrop } from '../shared/illustrations/PageBackdrop.js';
import { useLanguage } from '../shared/i18n/useLanguage.js';
import { useTheme } from '../shared/theme/useTheme.js';
import { Button } from '../shared/ui/index.js';

export function AppLayout(): ReactElement {
  const { user, impersonatedBy, canAdminister, signOut, stopImpersonating } = useAuth();
  const { language, t, setLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="app-shell">
      <PageBackdrop />
      {impersonatedBy === null ? null : (
        <ImpersonationBanner
          viewing={user?.username ?? t.nav.somebody}
          administrator={impersonatedBy.username}
          onStop={stopImpersonating}
        />
      )}

      <header className="app-header">
        <NavLink to="/" className="app-brand">
          <BrandMark />
          {t.nav.brand}
        </NavLink>
        <nav className="app-nav" aria-label={t.nav.mainNav}>
          <NavLink to="/sojourns">{t.nav.sojourns}</NavLink>
          <NavLink to="/calendar">{t.nav.calendar}</NavLink>
          <NavLink to="/houses">{t.nav.houses}</NavLink>
          <NavLink to="/rooms">{t.nav.rooms}</NavLink>
          <NavLink to="/guests">{t.nav.guests}</NavLink>
          {canAdminister ? <NavLink to="/admin">{t.nav.admin}</NavLink> : null}
        </nav>
        <div className="lang-toggle" role="group" aria-label={t.nav.language}>
          <button
            type="button"
            aria-pressed={language === 'en'}
            aria-label={t.nav.languageToggle('en')}
            onClick={() => setLanguage('en')}
          >
            EN
          </button>
          <button
            type="button"
            aria-pressed={language === 'fr'}
            aria-label={t.nav.languageToggle('fr')}
            onClick={() => setLanguage('fr')}
          >
            FR
          </button>
        </div>
        <button
          type="button"
          className="theme-toggle"
          aria-label={t.nav.themeToggle(theme === 'dark' ? 'light' : 'dark')}
          onClick={toggleTheme}
        >
          <span aria-hidden="true">{theme === 'dark' ? '🌙' : '☀️'}</span>
        </button>
        <div className="app-user">
          <span>
            {user?.username}
            {user === null || user.householdName === user.username ? null : (
              <span className="card__meta"> · {user.householdName}</span>
            )}
          </span>
          <Button variant="secondary" small onClick={signOut}>
            {t.nav.signOut}
          </Button>
        </div>
      </header>

      <main className="app-main">
        <DoorTransitionProvider>
          <Outlet />
        </DoorTransitionProvider>
      </main>
    </div>
  );
}

/**
 * A borrowed session must never be mistaken for your own, so this sits above
 * everything, in a colour nothing else uses, and cannot be dismissed — only
 * ended.
 */
function ImpersonationBanner({
  viewing,
  administrator,
  onStop,
}: {
  viewing: string;
  administrator: string;
  onStop: () => void;
}): ReactElement {
  const { t } = useLanguage();
  return (
    <div className="impersonation-banner" role="status">
      <span>{t.nav.viewingAs(viewing, administrator)}</span>
      <Button small variant="secondary" onClick={onStop}>
        {t.nav.stopViewingAs(viewing)}
      </Button>
    </div>
  );
}
